'use client';

import React, { useState, useRef, useEffect } from 'react';
import RGPSHeader from '@/components/RGPSHeader';
import RGPSFooter from '@/components/RGPSFooter';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ApplicationForm {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  city: string;
  dob: string;
  position: string;
  education: string;
  employmentStatus: string;
  transportation: string;
  driversLicense: string;
  salesExperience: string;
  customerServiceExp: string;
  availability: string;
  resume: File | null;
  aboutYourself: string;
  whyRGPS: string;
  greatFit: string;
  references: string;
  certified: boolean;
}

// ─── Intersection Observer Hook ───────────────────────────────────────────────

function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
}

// ─── Animated Section Wrapper ─────────────────────────────────────────────────

function AnimSection({ children, className = '', delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const { ref, inView } = useInView();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0)' : 'translateY(32px)',
        transition: `opacity 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}ms, transform 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function CareersPage() {
  const formRef = useRef<HTMLDivElement>(null);
  const salesRef = useRef<HTMLDivElement>(null);

  const [form, setForm] = useState<ApplicationForm>({
    firstName: '', lastName: '', phone: '', email: '', city: '', dob: '',
    position: '', education: '', employmentStatus: '', transportation: '',
    driversLicense: '', salesExperience: '', customerServiceExp: '',
    availability: '', resume: null, aboutYourself: '', whyRGPS: '',
    greatFit: '', references: '', certified: false,
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Calculate form progress
  useEffect(() => {
    const fields = [
      form.firstName, form.lastName, form.phone, form.email, form.city,
      form.dob, form.position, form.education, form.employmentStatus,
      form.transportation, form.driversLicense, form.availability,
      form.aboutYourself, form.whyRGPS, form.greatFit,
    ];
    const filled = fields.filter(f => f.trim() !== '').length;
    setProgress(Math.round((filled / fields.length) * 100));
  }, [form]);

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const scrollToSales = () => salesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      setForm(prev => ({ ...prev, [name]: (e.target as HTMLInputElement).checked }));
    } else {
      setForm(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setForm(prev => ({ ...prev, resume: file }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.certified) {
      setSubmitError('Please certify that all information provided is accurate.');
      return;
    }
    setLoading(true);
    setSubmitError(null);

    try {
      const body = new FormData();
      Object.entries(form).forEach(([key, val]) => {
        if (key === 'resume' && val instanceof File) {
          body.append('resume', val);
        } else if (key !== 'resume') {
          body.append(key, String(val));
        }
      });

      const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
      const supabaseKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;

      const res = await fetch(`${supabaseUrl}/functions/v1/send-career-application`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${supabaseKey}` },
        body,
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Submission failed. Please try again.');
      }

      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: unknown) {
      setSubmitError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ─── Success Screen ──────────────────────────────────────────────────────────
  if (submitted) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <RGPSHeader />
        <div className="flex-1 flex items-center justify-center px-4 py-32">
          <div className="text-center max-w-lg">
            <div className="w-24 h-24 rounded-full bg-teal-light flex items-center justify-center mx-auto mb-8 animate-bounce">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#0ABFBC" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <h1 className="text-4xl font-bold text-dark-text mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Thank You For Applying!
            </h1>
            <p className="text-gray-text text-lg leading-relaxed mb-6">
              We've received your application and truly appreciate your interest in joining Rio Grande Property Solutions.
            </p>
            <p className="text-gray-text leading-relaxed mb-8">
              Our hiring team will carefully review your application. If your experience and qualifications match our current or future opportunities, we'll reach out to schedule the next step.
            </p>
            <a href="/home" className="inline-flex items-center gap-2 bg-teal hover:bg-teal-dark text-white font-bold px-8 py-4 rounded-full transition-all shadow-teal">
              Back to Home
            </a>
          </div>
        </div>
        <RGPSFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <RGPSHeader />

      {/* ── HERO ──────────────────────────────────────────────────────────────── */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" />
        {/* Decorative overlay pattern */}
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'radial-gradient(circle at 25% 50%, #0ABFBC 0%, transparent 50%), radial-gradient(circle at 75% 20%, #16D4E8 0%, transparent 40%)',
        }} />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/60" />

        {/* Floating decorative elements */}
        <div className="absolute top-32 left-10 w-64 h-64 rounded-full bg-teal/10 blur-3xl animate-float" />
        <div className="absolute bottom-20 right-10 w-96 h-96 rounded-full bg-teal/5 blur-3xl" style={{ animationDelay: '2s' }} />

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 text-center pt-24 pb-16">
          <div className="inline-flex items-center gap-2 bg-teal/20 backdrop-blur-sm border border-teal/30 text-teal px-4 py-2 rounded-full text-sm font-semibold mb-8">
            <span className="w-2 h-2 rounded-full bg-teal animate-pulse" />
            Now Hiring in New Mexico
          </div>

          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-white leading-tight mb-6" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", letterSpacing: '-0.02em' }}>
            Build Your Career With<br />
            <span className="text-transparent bg-clip-text" style={{ backgroundImage: 'linear-gradient(135deg, #0ABFBC, #16D4E8)' }}>
              Rio Grande Property Solutions
            </span>
          </h1>

          <p className="text-white/80 text-lg sm:text-xl leading-relaxed max-w-3xl mx-auto mb-10">
            We're more than an exterior cleaning company—we're building a team of motivated people who want to grow, earn, and build something meaningful. Whether you're looking to become a top-performing sales representative or join our field team as we expand, we'd love to meet you.
          </p>

          {/* Trust Badges */}
          <div className="flex flex-wrap items-center justify-center gap-4 mb-10">
            {['Flexible Schedule', 'Uncapped Earning Potential', 'Opportunities for Growth'].map((badge) => (
              <div key={badge} className="flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white px-4 py-2.5 rounded-full text-sm font-semibold">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0ABFBC" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                {badge}
              </div>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={scrollToForm}
              className="w-full sm:w-auto bg-teal hover:bg-teal-dark text-white font-bold text-lg px-10 py-4 rounded-full transition-all shadow-teal hover:shadow-teal-lg"
            >
              Apply Today
            </button>
            <button
              onClick={scrollToSales}
              className="w-full sm:w-auto bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/30 text-white font-bold text-lg px-10 py-4 rounded-full transition-all"
            >
              Learn About Sales
            </button>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/40">
          <span className="text-xs font-medium tracking-widest uppercase">Scroll</span>
          <div className="w-px h-8 bg-gradient-to-b from-white/40 to-transparent" />
        </div>
      </section>

      {/* ── COMPANY MISSION ───────────────────────────────────────────────────── */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left */}
            <AnimSection>
              <div className="inline-flex items-center gap-2 bg-teal-light text-teal px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-6">
                Our Story
              </div>
              <h2 className="text-4xl sm:text-5xl font-extrabold text-dark-text mb-6 leading-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                Why We Started Rio Grande Property Solutions
              </h2>
              <p className="text-gray-text text-lg leading-relaxed mb-5">
                Rio Grande Property Solutions was built with one goal in mind: provide exceptional exterior cleaning services while creating real opportunities for hardworking people.
              </p>
              <p className="text-gray-text text-lg leading-relaxed mb-5">
                We believe great companies are built by great people. That's why we're committed to creating a team that values honesty, professionalism, reliability, and exceptional customer service.
              </p>
              <p className="text-gray-text text-lg leading-relaxed">
                As our company grows throughout New Mexico, we're looking for people who want to grow with us—not just work for us.
              </p>
            </AnimSection>

            {/* Right — Stat Cards */}
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: '📈', label: 'Growing Company', desc: 'Expanding throughout New Mexico' },
                { icon: '🚀', label: 'Career Advancement', desc: 'Real paths to leadership roles' },
                { icon: '🎓', label: 'Professional Training', desc: 'We invest in your development' },
                { icon: '🤝', label: 'Supportive Team', desc: 'We win together, always' },
              ].map((stat, i) => (
                <AnimSection key={stat.label} delay={i * 100}>
                  <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-card hover:shadow-card-lg hover:-translate-y-1 transition-all duration-300 group">
                    <div className="text-4xl mb-3">{stat.icon}</div>
                    <div className="font-bold text-dark-text text-lg mb-1 group-hover:text-teal transition-colors">{stat.label}</div>
                    <div className="text-gray-text text-sm leading-relaxed">{stat.desc}</div>
                  </div>
                </AnimSection>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── WHY JOIN US ───────────────────────────────────────────────────────── */}
      <section className="py-24 bg-light-gray">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <AnimSection className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-teal-light text-teal px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
              Benefits
            </div>
            <h2 className="text-4xl sm:text-5xl font-extrabold text-dark-text" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Why Join Our Team?
            </h2>
          </AnimSection>

          {/* Bento-style grid with varied sizes */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {/* Card 1 — Unlimited Growth (large) */}
            <AnimSection delay={0} className="lg:col-span-1">
              <div className="bg-white rounded-3xl p-8 shadow-card hover:shadow-card-lg hover:-translate-y-1 transition-all duration-300 h-full group">
                <div className="w-14 h-14 rounded-2xl bg-teal-light flex items-center justify-center mb-5 group-hover:bg-teal transition-colors">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#0ABFBC" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="group-hover:stroke-white transition-colors">
                    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" /><polyline points="17 6 23 6 23 12" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-dark-text mb-3">Unlimited Growth</h3>
                <p className="text-gray-text leading-relaxed">As our company expands, so will your opportunities for advancement into leadership, management, and specialized roles.</p>
              </div>
            </AnimSection>

            {/* Card 2 — Performance Pays */}
            <AnimSection delay={80}>
              <div className="bg-gradient-to-br from-teal to-teal-dark rounded-3xl p-8 shadow-teal hover:shadow-teal-lg hover:-translate-y-1 transition-all duration-300 h-full">
                <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center mb-5">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Performance Pays</h3>
                <p className="text-white/80 leading-relaxed">Hard work is rewarded. Your dedication directly impacts your earning potential with uncapped commissions.</p>
              </div>
            </AnimSection>

            {/* Card 3 — Flexible Scheduling */}
            <AnimSection delay={160}>
              <div className="bg-white rounded-3xl p-8 shadow-card hover:shadow-card-lg hover:-translate-y-1 transition-all duration-300 h-full group">
                <div className="w-14 h-14 rounded-2xl bg-teal-light flex items-center justify-center mb-5 group-hover:bg-teal transition-colors">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#0ABFBC" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="group-hover:stroke-white transition-colors">
                    <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-dark-text mb-3">Flexible Scheduling</h3>
                <p className="text-gray-text leading-relaxed">Perfect for students, parents, or anyone looking for flexible work while still having the opportunity to earn great money.</p>
              </div>
            </AnimSection>

            {/* Card 4 — Learn Valuable Skills (wide) */}
            <AnimSection delay={240} className="md:col-span-2 lg:col-span-1">
              <div className="bg-dark-text rounded-3xl p-8 shadow-card-lg hover:-translate-y-1 transition-all duration-300 h-full">
                <div className="w-14 h-14 rounded-2xl bg-teal/20 flex items-center justify-center mb-5">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#0ABFBC" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z" /><path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Learn Valuable Skills</h3>
                <div className="grid grid-cols-2 gap-2">
                  {['Sales', 'Communication', 'Leadership', 'Customer Service', 'Marketing', 'Time Management', 'Business Development'].map(skill => (
                    <div key={skill} className="flex items-center gap-2 text-white/70 text-sm">
                      <span className="w-1.5 h-1.5 rounded-full bg-teal flex-shrink-0" />
                      {skill}
                    </div>
                  ))}
                </div>
              </div>
            </AnimSection>

            {/* Card 5 — Positive Team */}
            <AnimSection delay={320}>
              <div className="bg-white rounded-3xl p-8 shadow-card hover:shadow-card-lg hover:-translate-y-1 transition-all duration-300 h-full group">
                <div className="w-14 h-14 rounded-2xl bg-teal-light flex items-center justify-center mb-5 group-hover:bg-teal transition-colors">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#0ABFBC" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="group-hover:stroke-white transition-colors">
                    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 00-3-3.87" /><path d="M16 3.13a4 4 0 010 7.75" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-dark-text mb-3">Positive Team Environment</h3>
                <p className="text-gray-text leading-relaxed">We work together, support each other, and celebrate wins as a team. No toxic competition—just shared success.</p>
              </div>
            </AnimSection>

            {/* Card 6 — Build Something Bigger */}
            <AnimSection delay={400}>
              <div className="bg-white rounded-3xl p-8 shadow-card hover:shadow-card-lg hover:-translate-y-1 transition-all duration-300 h-full group border-2 border-teal/20 hover:border-teal/50">
                <div className="w-14 h-14 rounded-2xl bg-teal-light flex items-center justify-center mb-5 group-hover:bg-teal transition-colors">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#0ABFBC" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="group-hover:stroke-white transition-colors">
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-dark-text mb-3">Build Something Bigger</h3>
                <p className="text-gray-text leading-relaxed">This isn't just another job. You'll help build one of New Mexico's fastest-growing exterior cleaning companies from the ground up.</p>
              </div>
            </AnimSection>
          </div>
        </div>
      </section>

      {/* ── NOW HIRING ────────────────────────────────────────────────────────── */}
      <section className="py-24 bg-white" ref={salesRef}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <AnimSection className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-teal-light text-teal px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
              Open Positions
            </div>
            <h2 className="text-4xl sm:text-5xl font-extrabold text-dark-text" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Current Opportunities
            </h2>
          </AnimSection>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Sales Rep — Featured Large Card */}
            <AnimSection className="lg:col-span-2">
              <div className="bg-white border-2 border-teal/30 rounded-3xl p-8 sm:p-10 shadow-card-lg h-full">
                {/* Header */}
                <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
                  <div>
                    <div className="inline-flex items-center gap-1.5 bg-teal text-white px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-3">
                      ⭐ NOW HIRING
                    </div>
                    <h3 className="text-3xl font-extrabold text-dark-text" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                      Door-to-Door Sales Representative
                    </h3>
                  </div>
                  <div className="flex items-center gap-2 bg-teal-light text-teal px-4 py-2 rounded-full text-sm font-semibold">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" />
                    </svg>
                    New Mexico
                  </div>
                </div>

                <p className="text-gray-text text-lg leading-relaxed mb-8 pb-8 border-b border-gray-100">
                  As a Door-to-Door Sales Representative, you'll represent Rio Grande Property Solutions by introducing homeowners to our services, educating them on the benefits of professional exterior cleaning, and scheduling free estimates. No high-pressure sales—your goal is simply to start conversations, build relationships, and generate opportunities.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mb-8">
                  {/* Responsibilities */}
                  <div>
                    <h4 className="font-bold text-dark-text text-base mb-4 flex items-center gap-2">
                      <span className="w-6 h-6 rounded-lg bg-teal-light flex items-center justify-center">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#0ABFBC" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 11 12 14 22 4" /><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" /></svg>
                      </span>
                      Responsibilities
                    </h4>
                    <ul className="space-y-2">
                      {['Professionally approach homeowners', 'Explain our services confidently', 'Schedule free estimates', 'Build trust with customers', 'Answer basic questions', 'Maintain a positive attitude', 'Track leads accurately', 'Represent the company professionally'].map(r => (
                        <li key={r} className="flex items-start gap-2 text-gray-text text-sm">
                          <span className="w-1.5 h-1.5 rounded-full bg-teal mt-1.5 flex-shrink-0" />
                          {r}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* What We're Looking For */}
                  <div>
                    <h4 className="font-bold text-dark-text text-base mb-4 flex items-center gap-2">
                      <span className="w-6 h-6 rounded-lg bg-teal-light flex items-center justify-center">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#0ABFBC" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                      </span>
                      What We're Looking For
                    </h4>
                    <ul className="space-y-2">
                      {['Positive attitude', 'Strong communication skills', 'Coachable mindset', 'Comfortable talking with new people', 'Reliable transportation preferred', 'Self-motivated', 'Professional appearance', 'Goal-oriented'].map(r => (
                        <li key={r} className="flex items-start gap-2 text-gray-text text-sm">
                          <span className="w-1.5 h-1.5 rounded-full bg-teal mt-1.5 flex-shrink-0" />
                          {r}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Preferred Qualifications */}
                <div className="bg-light-gray rounded-2xl p-5 mb-8">
                  <h4 className="font-bold text-dark-text text-sm mb-3">Preferred Qualifications</h4>
                  <div className="flex flex-wrap gap-2">
                    {['Sales experience (not required)', 'Customer service experience', 'Sports or team experience', 'Leadership experience', 'Entrepreneurial mindset'].map(q => (
                      <span key={q} className="bg-white border border-gray-200 text-gray-text text-xs px-3 py-1.5 rounded-full">{q}</span>
                    ))}
                  </div>
                </div>

                {/* Compensation */}
                <div className="bg-gradient-to-r from-teal/10 to-teal/5 border border-teal/20 rounded-2xl p-5 mb-8">
                  <h4 className="font-bold text-dark-text text-sm mb-2 flex items-center gap-2">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0ABFBC" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
                    </svg>
                    Compensation
                  </h4>
                  <p className="text-dark-text font-semibold text-base mb-1">Competitive commission-based pay with unlimited earning potential.</p>
                  <p className="text-gray-text text-sm">The harder you work, the more opportunities you create. Top performers will have opportunities to grow into leadership positions as the company expands.</p>
                </div>

                <button
                  onClick={scrollToForm}
                  className="w-full sm:w-auto bg-teal hover:bg-teal-dark text-white font-bold text-base px-10 py-4 rounded-full transition-all shadow-teal hover:shadow-teal-lg"
                >
                  Apply for Sales Position
                </button>
              </div>
            </AnimSection>

            {/* Field Technician — Coming Soon */}
            <AnimSection delay={200}>
              <div className="bg-white border border-gray-200 rounded-3xl p-8 shadow-card h-full flex flex-col">
                <div className="inline-flex items-center gap-1.5 bg-gray-100 text-gray-500 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-4 w-fit">
                  🔒 Coming Soon
                </div>
                <h3 className="text-2xl font-extrabold text-dark-text mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  Field Technician
                </h3>
                <p className="text-gray-text leading-relaxed mb-6">
                  Technician positions are not currently available, but we encourage ambitious applicants to apply early for future opportunities as we expand our service offerings throughout New Mexico.
                </p>

                <div className="bg-light-gray rounded-2xl p-5 mb-6 flex-1">
                  <h4 className="font-bold text-dark-text text-sm mb-3">Future Responsibilities May Include</h4>
                  <ul className="space-y-2">
                    {['Driveway Cleaning', 'Trash Bin Cleaning', 'Commercial Property Cleaning', 'Pressure Washing', 'Equipment Maintenance', 'Customer Service'].map(r => (
                      <li key={r} className="flex items-center gap-2 text-gray-text text-sm">
                        <span className="w-1.5 h-1.5 rounded-full bg-gray-300 flex-shrink-0" />
                        {r}
                      </li>
                    ))}
                  </ul>
                </div>

                <button
                  onClick={scrollToForm}
                  className="w-full bg-gray-100 hover:bg-gray-200 text-gray-600 font-bold text-sm px-6 py-3.5 rounded-full transition-all"
                >
                  Join Future Hiring List
                </button>
              </div>
            </AnimSection>
          </div>
        </div>
      </section>

      {/* ── SUCCESS TIMELINE ──────────────────────────────────────────────────── */}
      <section className="py-24 bg-dark-text overflow-hidden">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <AnimSection className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-teal/20 text-teal px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
              Your Journey
            </div>
            <h2 className="text-4xl sm:text-5xl font-extrabold text-white" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              What Success Looks Like
            </h2>
          </AnimSection>

          {/* Horizontal timeline on desktop, vertical on mobile */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { period: 'Week 1', title: 'Training', desc: 'Learn our process, products, and approach to customer conversations.', color: 'from-teal/30 to-teal/10' },
              { period: 'Week 2', title: 'Begin Meeting Homeowners', desc: 'Hit the field with confidence and start building your pipeline.', color: 'from-teal/50 to-teal/20' },
              { period: 'Month 1', title: 'Build Confidence', desc: 'Improve your communication, refine your pitch, and close your first deals.', color: 'from-teal/70 to-teal/30' },
              { period: 'Month 3+', title: 'Top Performer', desc: 'Become one of our top representatives and unlock advancement opportunities.', color: 'from-teal to-teal-dark' },
            ].map((step, i) => (
              <AnimSection key={step.period} delay={i * 120}>
                <div className={`relative bg-gradient-to-br ${step.color} border border-white/10 rounded-3xl p-6 h-full`}>
                  <div className="absolute -top-3 left-6 bg-teal text-white text-xs font-bold px-3 py-1 rounded-full">
                    {step.period}
                  </div>
                  <div className="mt-4">
                    <h4 className="font-bold text-white text-lg mb-2">{step.title}</h4>
                    <p className="text-white/70 text-sm leading-relaxed">{step.desc}</p>
                  </div>
                  {i < 3 && (
                    <div className="hidden lg:block absolute -right-3 top-1/2 -translate-y-1/2 z-10">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0ABFBC" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="9 18 15 12 9 6" />
                      </svg>
                    </div>
                  )}
                </div>
              </AnimSection>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHO SHOULD APPLY ──────────────────────────────────────────────────── */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <AnimSection className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-teal-light text-teal px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
              Is This You?
            </div>
            <h2 className="text-4xl sm:text-5xl font-extrabold text-dark-text" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Who Should Apply?
            </h2>
          </AnimSection>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {[
              { icon: '🎓', label: 'College Students' },
              { icon: '🏆', label: 'Recent Graduates' },
              { icon: '💰', label: 'People Wanting Extra Income' },
              { icon: '⚡', label: 'Motivated Self-Starters' },
              { icon: '🚀', label: 'Future Entrepreneurs' },
              { icon: '🥇', label: 'Competitive Individuals' },
              { icon: '📊', label: 'Commission-Driven Earners' },
              { icon: '🌟', label: 'Anyone Ready to Grow' },
            ].map((item, i) => (
              <AnimSection key={item.label} delay={i * 60}>
                <div className="bg-white border border-gray-100 rounded-2xl p-5 text-center shadow-card hover:shadow-card-lg hover:-translate-y-1 hover:border-teal/30 transition-all duration-300 group">
                  <div className="text-3xl mb-3">{item.icon}</div>
                  <div className="font-semibold text-dark-text text-sm group-hover:text-teal transition-colors">{item.label}</div>
                </div>
              </AnimSection>
            ))}
          </div>
        </div>
      </section>

      {/* ── CORE VALUES ───────────────────────────────────────────────────────── */}
      <section className="py-24 bg-light-gray">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <AnimSection className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-teal-light text-teal px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
              What We Stand For
            </div>
            <h2 className="text-4xl sm:text-5xl font-extrabold text-dark-text" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Our Core Values
            </h2>
          </AnimSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: '⚖️', value: 'Integrity', desc: 'We always do what\'s right, even when no one is watching.', accent: 'bg-blue-50 border-blue-100' },
              { icon: '👔', value: 'Professionalism', desc: 'We represent ourselves and our company with pride in everything we do.', accent: 'bg-purple-50 border-purple-100' },
              { icon: '📈', value: 'Growth', desc: 'We never stop learning, improving, and pushing ourselves forward.', accent: 'bg-teal-light border-teal/20' },
              { icon: '⭐', value: 'Customer First', desc: 'Every customer deserves exceptional service and a five-star experience.', accent: 'bg-amber-50 border-amber-100' },
            ].map((val, i) => (
              <AnimSection key={val.value} delay={i * 100}>
                <div className={`${val.accent} border rounded-3xl p-8 hover:shadow-card-lg hover:-translate-y-1 transition-all duration-300 h-full`}>
                  <div className="text-4xl mb-4">{val.icon}</div>
                  <h3 className="text-xl font-bold text-dark-text mb-3">{val.value}</h3>
                  <p className="text-gray-text leading-relaxed">{val.desc}</p>
                </div>
              </AnimSection>
            ))}
          </div>
        </div>
      </section>

      {/* ── APPLICATION FORM ──────────────────────────────────────────────────── */}
      <section className="py-24 bg-white" ref={formRef} id="careers-form">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <AnimSection className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-teal-light text-teal px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
              Apply Now
            </div>
            <h2 className="text-4xl sm:text-5xl font-extrabold text-dark-text mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Start Your Application
            </h2>
            <p className="text-gray-text text-lg">
              Take the first step toward joining Rio Grande Property Solutions.
            </p>
          </AnimSection>

          {/* Progress Bar */}
          <AnimSection className="mb-8">
            <div className="bg-light-gray rounded-2xl p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-dark-text">Application Progress</span>
                <span className="text-sm font-bold text-teal">{progress}%</span>
              </div>
              <div className="h-2.5 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-teal to-teal-dark rounded-full transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="text-xs text-gray-text mt-2">Complete all required fields to submit your application.</p>
            </div>
          </AnimSection>

          <AnimSection>
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Personal Info */}
              <div className="bg-white border border-gray-100 rounded-3xl p-8 shadow-card">
                <h3 className="text-lg font-bold text-dark-text mb-6 flex items-center gap-2">
                  <span className="w-8 h-8 rounded-xl bg-teal-light flex items-center justify-center text-teal font-bold text-sm">1</span>
                  Personal Information
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-semibold text-dark-text mb-2">First Name <span className="text-red-500">*</span></label>
                    <input name="firstName" value={form.firstName} onChange={handleChange} required placeholder="John"
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-dark-text placeholder-gray-400 focus:outline-none focus:border-teal focus:ring-2 focus:ring-teal/20 transition-all" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-dark-text mb-2">Last Name <span className="text-red-500">*</span></label>
                    <input name="lastName" value={form.lastName} onChange={handleChange} required placeholder="Smith"
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-dark-text placeholder-gray-400 focus:outline-none focus:border-teal focus:ring-2 focus:ring-teal/20 transition-all" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-dark-text mb-2">Phone Number <span className="text-red-500">*</span></label>
                    <input name="phone" value={form.phone} onChange={handleChange} required type="tel" placeholder="(505) 000-0000"
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-dark-text placeholder-gray-400 focus:outline-none focus:border-teal focus:ring-2 focus:ring-teal/20 transition-all" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-dark-text mb-2">Email Address <span className="text-red-500">*</span></label>
                    <input name="email" value={form.email} onChange={handleChange} required type="email" placeholder="john@example.com"
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-dark-text placeholder-gray-400 focus:outline-none focus:border-teal focus:ring-2 focus:ring-teal/20 transition-all" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-dark-text mb-2">City <span className="text-red-500">*</span></label>
                    <input name="city" value={form.city} onChange={handleChange} required placeholder="Albuquerque"
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-dark-text placeholder-gray-400 focus:outline-none focus:border-teal focus:ring-2 focus:ring-teal/20 transition-all" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-dark-text mb-2">Date of Birth <span className="text-red-500">*</span></label>
                    <input name="dob" value={form.dob} onChange={handleChange} required type="date"
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-dark-text focus:outline-none focus:border-teal focus:ring-2 focus:ring-teal/20 transition-all" />
                  </div>
                </div>
              </div>

              {/* Position & Background */}
              <div className="bg-white border border-gray-100 rounded-3xl p-8 shadow-card">
                <h3 className="text-lg font-bold text-dark-text mb-6 flex items-center gap-2">
                  <span className="w-8 h-8 rounded-xl bg-teal-light flex items-center justify-center text-teal font-bold text-sm">2</span>
                  Position & Background
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-semibold text-dark-text mb-2">Position Applying For <span className="text-red-500">*</span></label>
                    <select name="position" value={form.position} onChange={handleChange} required
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-dark-text focus:outline-none focus:border-teal focus:ring-2 focus:ring-teal/20 transition-all bg-white">
                      <option value="">Select a position</option>
                      <option value="sales-rep">Door-to-Door Sales Representative</option>
                      <option value="future-technician">Future Field Technician (Hiring List)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-dark-text mb-2">Highest Education <span className="text-red-500">*</span></label>
                    <select name="education" value={form.education} onChange={handleChange} required
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-dark-text focus:outline-none focus:border-teal focus:ring-2 focus:ring-teal/20 transition-all bg-white">
                      <option value="">Select education level</option>
                      <option value="high-school">High School / GED</option>
                      <option value="some-college">Some College</option>
                      <option value="associates">Associate's Degree</option>
                      <option value="bachelors">Bachelor's Degree</option>
                      <option value="masters">Master's Degree or Higher</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-dark-text mb-2">Current Employment Status <span className="text-red-500">*</span></label>
                    <select name="employmentStatus" value={form.employmentStatus} onChange={handleChange} required
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-dark-text focus:outline-none focus:border-teal focus:ring-2 focus:ring-teal/20 transition-all bg-white">
                      <option value="">Select status</option>
                      <option value="employed-full">Employed Full-Time</option>
                      <option value="employed-part">Employed Part-Time</option>
                      <option value="unemployed">Unemployed</option>
                      <option value="student">Student</option>
                      <option value="self-employed">Self-Employed</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-dark-text mb-2">Availability <span className="text-red-500">*</span></label>
                    <select name="availability" value={form.availability} onChange={handleChange} required
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-dark-text focus:outline-none focus:border-teal focus:ring-2 focus:ring-teal/20 transition-all bg-white">
                      <option value="">Select availability</option>
                      <option value="full-time">Full-Time (40+ hrs/week)</option>
                      <option value="part-time">Part-Time (20–39 hrs/week)</option>
                      <option value="flexible">Flexible / As Needed</option>
                      <option value="weekends">Weekends Only</option>
                      <option value="evenings">Evenings Only</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Qualifications */}
              <div className="bg-white border border-gray-100 rounded-3xl p-8 shadow-card">
                <h3 className="text-lg font-bold text-dark-text mb-6 flex items-center gap-2">
                  <span className="w-8 h-8 rounded-xl bg-teal-light flex items-center justify-center text-teal font-bold text-sm">3</span>
                  Qualifications
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-semibold text-dark-text mb-2">Reliable Transportation <span className="text-red-500">*</span></label>
                    <select name="transportation" value={form.transportation} onChange={handleChange} required
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-dark-text focus:outline-none focus:border-teal focus:ring-2 focus:ring-teal/20 transition-all bg-white">
                      <option value="">Select</option>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                      <option value="sometimes">Sometimes</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-dark-text mb-2">Driver's License <span className="text-red-500">*</span></label>
                    <select name="driversLicense" value={form.driversLicense} onChange={handleChange} required
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-dark-text focus:outline-none focus:border-teal focus:ring-2 focus:ring-teal/20 transition-all bg-white">
                      <option value="">Select</option>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-dark-text mb-2">Sales Experience</label>
                    <select name="salesExperience" value={form.salesExperience} onChange={handleChange}
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-dark-text focus:outline-none focus:border-teal focus:ring-2 focus:ring-teal/20 transition-all bg-white">
                      <option value="">Select</option>
                      <option value="none">No experience (that's okay!)</option>
                      <option value="some">Some experience (less than 1 year)</option>
                      <option value="1-2">1–2 years</option>
                      <option value="3+">3+ years</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-dark-text mb-2">Customer Service Experience</label>
                    <select name="customerServiceExp" value={form.customerServiceExp} onChange={handleChange}
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-dark-text focus:outline-none focus:border-teal focus:ring-2 focus:ring-teal/20 transition-all bg-white">
                      <option value="">Select</option>
                      <option value="none">No experience</option>
                      <option value="some">Some experience</option>
                      <option value="1-2">1–2 years</option>
                      <option value="3+">3+ years</option>
                    </select>
                  </div>
                </div>

                {/* Resume Upload */}
                <div className="mt-5">
                  <label className="block text-sm font-semibold text-dark-text mb-2">Resume Upload (Optional)</label>
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-gray-200 hover:border-teal rounded-2xl p-6 text-center cursor-pointer transition-all group"
                  >
                    <input ref={fileInputRef} type="file" accept=".pdf,.doc,.docx" onChange={handleFileChange} className="hidden" />
                    {form.resume ? (
                      <div className="flex items-center justify-center gap-3">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0ABFBC" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" />
                        </svg>
                        <span className="text-teal font-semibold text-sm">{form.resume.name}</span>
                        <button type="button" onClick={(e) => { e.stopPropagation(); setForm(p => ({ ...p, resume: null })); }} className="text-gray-400 hover:text-red-500 transition-colors">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                        </button>
                      </div>
                    ) : (
                      <div>
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="mx-auto mb-2 group-hover:stroke-teal transition-colors">
                          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" />
                        </svg>
                        <p className="text-gray-text text-sm">Click to upload your resume</p>
                        <p className="text-gray-400 text-xs mt-1">PDF, DOC, DOCX accepted</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Personal Statements */}
              <div className="bg-white border border-gray-100 rounded-3xl p-8 shadow-card">
                <h3 className="text-lg font-bold text-dark-text mb-6 flex items-center gap-2">
                  <span className="w-8 h-8 rounded-xl bg-teal-light flex items-center justify-center text-teal font-bold text-sm">4</span>
                  Tell Us About Yourself
                </h3>
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-semibold text-dark-text mb-2">About Yourself <span className="text-red-500">*</span></label>
                    <textarea name="aboutYourself" value={form.aboutYourself} onChange={handleChange} required rows={4}
                      placeholder="Tell us a bit about yourself, your background, and what you're looking for..."
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-dark-text placeholder-gray-400 focus:outline-none focus:border-teal focus:ring-2 focus:ring-teal/20 transition-all resize-none" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-dark-text mb-2">Why Do You Want To Work With Rio Grande Property Solutions? <span className="text-red-500">*</span></label>
                    <textarea name="whyRGPS" value={form.whyRGPS} onChange={handleChange} required rows={4}
                      placeholder="What drew you to our company? What excites you about this opportunity?"
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-dark-text placeholder-gray-400 focus:outline-none focus:border-teal focus:ring-2 focus:ring-teal/20 transition-all resize-none" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-dark-text mb-2">What Makes You A Great Fit? <span className="text-red-500">*</span></label>
                    <textarea name="greatFit" value={form.greatFit} onChange={handleChange} required rows={4}
                      placeholder="What skills, experiences, or qualities make you the right person for this role?"
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-dark-text placeholder-gray-400 focus:outline-none focus:border-teal focus:ring-2 focus:ring-teal/20 transition-all resize-none" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-dark-text mb-2">Professional References (Optional)</label>
                    <textarea name="references" value={form.references} onChange={handleChange} rows={3}
                      placeholder="Name, relationship, and contact info for 1–2 professional references..."
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-dark-text placeholder-gray-400 focus:outline-none focus:border-teal focus:ring-2 focus:ring-teal/20 transition-all resize-none" />
                  </div>
                </div>
              </div>

              {/* Certification & Submit */}
              <div className="bg-white border border-gray-100 rounded-3xl p-8 shadow-card">
                <label className="flex items-start gap-3 cursor-pointer group mb-6">
                  <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all ${form.certified ? 'bg-teal border-teal' : 'border-gray-300 group-hover:border-teal'}`}>
                    {form.certified && (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </div>
                  <input type="checkbox" name="certified" checked={form.certified} onChange={handleChange} className="sr-only" />
                  <span className="text-gray-text text-sm leading-relaxed">
                    I certify that all information provided in this application is accurate and complete to the best of my knowledge. I understand that any misrepresentation may result in disqualification from consideration.
                  </span>
                </label>

                {submitError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm mb-5">
                    {submitError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading || !form.certified}
                  className="w-full bg-teal hover:bg-teal-dark disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-bold text-lg py-5 rounded-2xl transition-all shadow-teal hover:shadow-teal-lg flex items-center justify-center gap-3"
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 12a9 9 0 11-6.219-8.56" />
                      </svg>
                      Submitting Your Application...
                    </>
                  ) : (
                    <>
                      Submit My Application
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
                      </svg>
                    </>
                  )}
                </button>
              </div>
            </form>
          </AnimSection>
        </div>
      </section>

      {/* ── FINAL CTA ─────────────────────────────────────────────────────────── */}
      <section className="py-24 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'radial-gradient(circle at 30% 50%, #0ABFBC 0%, transparent 50%), radial-gradient(circle at 70% 30%, #16D4E8 0%, transparent 40%)',
        }} />
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <AnimSection>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 leading-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Your Next Opportunity<br />
              <span className="text-transparent bg-clip-text" style={{ backgroundImage: 'linear-gradient(135deg, #0ABFBC, #16D4E8)' }}>
                Starts Here.
              </span>
            </h2>
            <p className="text-white/70 text-lg leading-relaxed max-w-2xl mx-auto mb-10">
              If you're motivated, dependable, and ready to grow with an ambitious company, we'd love to hear from you. Whether you're looking for flexible work, unlimited earning potential, or a long-term career, Rio Grande Property Solutions is building a team that values hard work and great people.
            </p>
            <button
              onClick={scrollToForm}
              className="bg-teal hover:bg-teal-dark text-white font-bold text-xl px-12 py-5 rounded-full transition-all shadow-teal hover:shadow-teal-lg"
            >
              Apply Today
            </button>
          </AnimSection>
        </div>
      </section>

      {/* Sticky Apply Button — Mobile */}
      <div className="fixed bottom-6 left-4 right-4 z-40 sm:hidden">
        <button
          onClick={scrollToForm}
          className="w-full bg-teal text-white font-bold py-4 rounded-2xl shadow-teal text-base flex items-center justify-center gap-2"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><polyline points="10 9 9 9 8 9" />
          </svg>
          Apply Now
        </button>
      </div>

      <RGPSFooter />
    </div>
  );
}
